import React from 'react';
import {
  isEmpty,
  each,
  replace,
  get,
  isArray,
  indexOf,
  forEach,
  find,
  size,
  startsWith,
} from 'lodash';
import { getOrCondition, getTernaryCondition } from 'common-ui-components/lib/core/utils/common';
import { DEFAULT_REDUX_KEY } from '../lib/dynamicStore/constants';
import {
  ERROR_CODE,
  SET_STORAGE_CART_ID,
  SET_STORAGE_CART_GUID,
  USER_STATE_LOGGED_IN,
  USER_TYPE_ANONYMOUS,
  COUNTRY_CODE_KEY,
  LOCALE_KEY,
  DEFAULT_COUNTRY_CODE,
  DEFAULT_LOCALE,
  ACCEPTED_CREDIT_CARDS,
  USER_ID_KEY,
  SOLD_TO_KEY,
  USER_ADDRESS_TYPE,
  FULL_ENABLED,
  PARTIAL_ENABLED,
  DEFAULT_PATH_FORMAT,
  EPROC_DEFAULT_PATH_FORMAT,
  PUNCHOUT_SETUP,
  userDetailsContract,
  USER_ADDRESS,
  QUICK_ORDER_ERROR,
  MAILING_ADDRESS_COUNTRY_CODE_KEY,
  EPROC_USER_ROLE,
  ECOMM_USER_ROLE,
  CART_URL,
  LOGIN_COOKIE,
  POST_ASTERISK_COUNTRY_CODES,
  QUICK_ORDER_URL,
  LOAD_SHOPPING_CART_DATA,
  QUOTE_ID_KEY,
  RECENTLY_SEARCHED_KEY,
  DISMISS_SYSTEM_NOTIFICATION_STORAGE,
  EPROC_RECENTLY_SEARCHED_KEY,
  CHECKOUT_URL,
} from '../global/constants';
import {
  PURCHASE_ORDER_ATTACHMENT_TYPE,
  TAX_EXEMPTION_ATTACHMENT_TYPE,
  ADDRESS_TYPE,
} from '../components/templates/Checkout/Checkout.constants';
import {
  setItem,
  getItem,
  removeItem,
  sessionRemoveItem,
  storageSessionClear,
  storageClear,
  sessionGetItem,
  sessionGetItemValue,
  sessionSetItemValue,
} from './storage';
import { defaultLabels, defaultConfig } from '../constants/labelConfig';
import { downloadExcelSheet } from './utilFunctions';

/* No changes are required in this file. */

/**
 * To handle an exception case where host names are different in dev & test environment of onprem and will be removed once it is decommissioned.
 */
const lowerEnvSitesArray = ['https://dev-www.waters.com', 'https://test-www.waters.com'];
export const getStore = () => {
  if (window[DEFAULT_REDUX_KEY]) {
    return window[DEFAULT_REDUX_KEY].getState();
  }
  return {};
};
export const isUserLoggedIn = () => {
  let status = false;
  const store = getStore();
  const userState = get(store, ['global', 'globalData', 'userState'], '');
  if (userState === USER_STATE_LOGGED_IN) {
    status = true;
  }
  return status;
};
export const isEProcurementApp = () => {
  const store = getStore();
  return get(store, ['global', 'globalData', 'isEprocurementApp'], undefined);
};
export const convertObjectToArray = (obj, takeKeys) =>
  Array.prototype.concat.apply([], takeKeys ? Object.keys(obj) : Object.values(obj)); // eslint-disable-line

export const parseQueryParams = pathname => {
  const search = pathname?.split('?')[1] || undefined;
  if (search) {
    const queryList = search.split('&');
    return queryList.reduce((accu, curr) => {
      const [key, value] = curr.split('=');
      return { ...accu, [key]: value.split('#')[0] };
    }, {});
  }
  return {};
};
export const cleanObject = obj => {
  const newObj = {};
  Object.keys(obj).forEach(key => {
    if (obj[key]) newObj[key] = obj[key];
  });
  return newObj;
};
export const getCookie = key => {
  const name = `${key}=`;
  const decodedCookie = decodeURIComponent(document.cookie);
  const ca = decodedCookie.split(';');
  for (let i = 0; i < ca.length; i += 1) {
    let c = ca[i];
    while (startsWith(c, ' ')) {
      c = c.substring(1);
    }
    if (startsWith(c, name)) {
      return c.substring(name.length, c.length);
    }
  }
  return '';
};
export const setUrlPathVariables = (url = '', pathVars = {}) => {
  let urlPath = url;
  if (!isEmpty(urlPath) && !isEmpty(pathVars)) {
    each(pathVars, (value, key) => {
      urlPath = replace(urlPath, `{${key}}`, encodeURIComponent(value));
    });
  }
  return urlPath;
};
export const createUrlSearchParams = (query = {}) => {
  const queryParams = [];
  const keys = Object.keys(query);
  for (let i = 0, l = keys.length; i < l; i += 1) {
    queryParams.push(`${keys[i]}=${query[keys[i]]}`);
  }
  return queryParams.join('&');
};
export const buildUrl = options => {
  if (typeof options === 'object') {
    const { pathname, query, pathVars } = options;
    let url = pathname;
    if (typeof pathVars === 'object' && !isEmpty(pathVars)) {
      url = setUrlPathVariables(url, pathVars);
    }
    if (typeof query === 'object' && !isEmpty(query)) {
      url += `?${createUrlSearchParams(query)}`;
    }
    return url;
  }
  return options;
};
export const removeChar = (str, ch) => {
  if (!str || !ch) return str;
  const reg = new RegExp(`\\${ch}{1,}`, 'gi');
  return str.replace(reg, x => (x === ch ? ' ' : ` ${ch} `));
};
export const elementOffset = el => {
  const rect = el.getBoundingClientRect();
  const scrollLeft = window.scrollX || document.documentElement.scrollLeft;
  const scrollTop = window.scrollY || document.documentElement.scrollTop;
  return { top: rect.top + scrollTop, left: rect.left + scrollLeft };
};
export const isIOS = () => {
  if (typeof window !== 'undefined') {
    return /iPhone|iPad|iPod/i.test(window.navigator.userAgent);
  }
  return false;
};
export const escapeHtml = (text = '') => text.replace(/&#039;/g, "'").replace(/%2C;/g, ',');
export const addDecimal = value => {
  let price = value;
  if (typeof price === 'string') {
    price = parseFloat(value);
  }
  return price.toFixed(2);
};
export const removeDecimal = value => (+value % 1 === 0 ? parseInt(value, 10) : value);
export const getLabelWithParam = (message = '', param = []) => {
  let labels = message;
  if (!isEmpty(labels) && !isEmpty(param)) {
    each(param, (value, key) => {
      labels = replace(labels, `{${key}}`, value);
    });
  }
  return labels;
};
const getCurrentDateInDDMMYYYY = () => {
  const time = new Date();
  const year = time.getFullYear();
  const month = `0${time.getMonth() + 1}`.slice(-2);
  const day = `0${time.getDate()}`.slice(-2);
  return `${day}-${month}-${year}`;
};
// this is cart specific feature and should be in the cart folder
// keeping this download utility in utils for the lack of better option
export const downloadXLSX = cartItems => {
  const header = ['SKU_NUMBER', 'QUANTITY', 'UNIT_PRICE', 'SKU_NAME'];
  const rows = [];
  const store = getStore();
  const includeTaxWithPrice = get(
    store,
    ['global', 'globalData', 'countryConfig', 'includeTaxWithPrice'],
    false
  );
  const emptyValue = '--';
  if (size(cartItems) > 0) {
    each(cartItems, item => {
      const price = includeTaxWithPrice
        ? get(item, ['customerPriceWithTax', 'formattedValue']) ||
          get(item, ['basePriceWithTax', 'formattedValue']) ||
          emptyValue
        : get(item, ['customerPrice', 'formattedValue']) ||
          get(item, ['basePrice', 'formattedValue']) ||
          emptyValue;
      const code = get(item, ['product', 'code'], emptyValue);
      const qty = get(item, ['quantity'], emptyValue);
      const productName = get(item, ['product', 'name'], emptyValue);
      rows.push({ SKU_NUMBER: code, QUANTITY: qty, UNIT_PRICE: price, SKU_NAME: productName });
    });
  } else {
    rows.push({});
  }

  const downloadDate = getCurrentDateInDDMMYYYY();
  downloadExcelSheet(rows, header, `downloadCart ${downloadDate}.xlsx`);
};

export const setCartKey = ({ code, guid }) => {
  const isUserLogin = isUserLoggedIn();
  if (isUserLogin) {
    setItem(SET_STORAGE_CART_ID, code);
    removeItem(SET_STORAGE_CART_GUID);
  } else {
    setItem(SET_STORAGE_CART_GUID, guid);
    removeItem(SET_STORAGE_CART_ID);
  }
};
export const mergeCart = key => {
  let value = key;
  let isCartMerge = false;
  if (isEmpty(key)) {
    const guid = getItem(SET_STORAGE_CART_GUID);
    value = isEmpty(guid) ? key : guid;
    isCartMerge = !isEmpty(guid);
  }
  return { cartKey: value, isCartMerge };
};
export const cartMergeStatus = () => {
  let status = false;
  const isUserLogin = isUserLoggedIn();
  if (isUserLogin) {
    const { isCartMerge } = mergeCart(getItem(SET_STORAGE_CART_ID));
    status = isCartMerge;
  }
  return status;
};
export const createCartStatus = key => !!isEmpty(key);
export const getCartKey = () => {
  const isUserLogin = isUserLoggedIn();
  let obj = {};
  if (isUserLogin) {
    const { cartKey } = mergeCart(getItem(SET_STORAGE_CART_ID));
    obj = cartKey;
  } else {
    obj = getItem(SET_STORAGE_CART_GUID);
  }
  return obj;
};
export const getDefaultSoldTo = soldToList => {
  if (soldToList === null || soldToList === undefined || soldToList.length <= 0) return {};
  const defaultSoldTo = soldToList.filter(item => item.soldToFlag === 1)[0];
  return defaultSoldTo || {};
};
export const getSoldToId = () => {
  const isUserLogin = isUserLoggedIn();
  const store = getStore();
  let soldToId = '';
  if (isUserLogin) {
    const soldToData = get(store, ['global', 'globalData', 'userDetails', 'soldTo'], '');
    if (!isEmpty(soldToData)) {
      soldToId = isArray(soldToData) ? getDefaultSoldTo(soldToData) : soldToData;
      soldToId = get(soldToId, ['customerNumber'], '');
      if (isEmpty(soldToId)) {
        soldToId = get(store, ['global', 'globalData', 'userDetails', 'dummySoldto'], '');
      }
    } else {
      soldToId = get(store, ['global', 'globalData', 'userDetails', 'dummySoldto'], '');
    }
  }
  return soldToId;
};
export const getDefaultLocaleCountry = () => {
  const store = getStore();
  const defaultPath = (isEProcurementApp() ? EPROC_DEFAULT_PATH_FORMAT : DEFAULT_PATH_FORMAT).split(
    '/'
  );
  const pathVars = {};
  let pathname = get(store, ['global', 'globalData', 'route', 'location', 'pathname'], undefined);
  pathname = pathname?.split('/') || [];
  forEach(pathname, (value, key) => {
    if (!isEmpty(value)) {
      pathVars[defaultPath[key]] = value;
    }
  });
  return pathVars;
};
const getLocaleCountry = () => {
  const pathVars = getDefaultLocaleCountry() || {};
  return get(pathVars, 'localeCountry', DEFAULT_COUNTRY_CODE);
};
const getLocale = () => {
  const pathVars = getDefaultLocaleCountry() || {};
  return get(pathVars, 'localeLanguage', DEFAULT_LOCALE);
};
export const getDefaultPathVar = () => {
  const store = getStore();
  const isUserLogin = isUserLoggedIn();
  let userType = USER_TYPE_ANONYMOUS;
  if (isUserLogin) {
    userType = get(store, ['global', 'globalData', 'userDetails', 'userId'], '');
  }
  const mailingAddressCountryCode = get(
    store,
    ['global', 'globalData', 'userDetails', MAILING_ADDRESS_COUNTRY_CODE_KEY],
    undefined
  );
  const localeCountry =
    mailingAddressCountryCode ||
    get(
      store,
      ['global', 'globalData', 'route', 'match', 'params', COUNTRY_CODE_KEY],
      getLocaleCountry()
    );
  const localeLanguage =
    get(store, ['global', 'globalData', 'userDetails', LOCALE_KEY]) ||
    get(store, ['global', 'globalData', 'route', 'match', 'params', LOCALE_KEY], getLocale());
  return {
    userType,
    localeCountry: localeCountry.toLowerCase(),
    localeLanguage: localeLanguage.toLowerCase(),
  };
};
export const countryName = (countryCode = '', countryData = {}, addressCountryName = '') => {
  const { sapCountryCode = '', name } = countryData || {};
  if (countryCode.toLowerCase() === sapCountryCode.toLowerCase()) {
    return name;
  }
  if (countryCode.toLowerCase() !== sapCountryCode.toLowerCase() && !isEmpty(addressCountryName)) {
    return addressCountryName;
  }
  return countryCode;
};
export const removeLastPartFromUrl = url => {
  const splitUrl = url.split('/');
  splitUrl.pop();
  return splitUrl.join('/');
};
export const getLastPartFromUrl = url => {
  const splitUrl = url.split('#')[0].split('/');
  return splitUrl.pop();
};
export const getUserId = () => {
  const store = getStore();
  const isUserLogin = isUserLoggedIn();
  let userId = 0;
  if (isUserLogin) {
    userId = get(store, ['global', 'globalData', 'userDetails', 'userId'], '');
  }
  return userId;
};
export const getLabelsConfigData = pageData => {
  // we are setting defaultLabels and defaultConfig for temp user
  // when all labels and config will deploy on AEM than remove defaultLabels and defaultConfig
  const labelsObj = { ...defaultLabels };
  const configObj = { ...defaultConfig };
  const labelData = pageData.find(item => item.name === 'root');
  const header = pageData.find(item => item.name === 'header');
  const signInLink = get(header, ['signInLink'], '');
  const createAccountLink = get(header, ['createAccountLink'], '');
  const myAccountLink = get(header, ['myAccountLink'], '');
  const { labels = {} } = labelData;
  const { labelList = {}, configList = {} } = labels;
  if (labelList) {
    each(labelList, value => {
      const { labelKey, labelValue } = value;
      labelsObj[labelKey] = labelValue;
    });
  }
  if (configList) {
    each(configList, value => {
      const { text, link } = value;
      configObj[text] = link;
    });
  }
  configObj.signInUrl = signInLink;
  configObj.createAccountUrl = createAccountLink;
  configObj.myAccountLink = myAccountLink;
  return { labels: labelsObj, config: configObj };
};
export const getCreditCardType = ({ cardNumber = '', cardType } = {}) => {
  if (cardNumber.length === 0) {
    return null;
  }
  let re = /^4/;
  if (re.exec(cardNumber) !== null) return ACCEPTED_CREDIT_CARDS.VISA;

  re = /^5[1-5]/;
  if (re.exec(cardNumber) != null) return ACCEPTED_CREDIT_CARDS.MAST;

  re = /^3[47]/;
  if (re.exec(cardNumber) != null) return ACCEPTED_CREDIT_CARDS.AMEX;

  re = /^(6011|622(12[6-9]|1[3-9]\d|[2-8]\d{2}|9[0-1]\d|92[0-5]|64[4-9])|65)/;
  if (re.exec(cardNumber) != null) return ACCEPTED_CREDIT_CARDS.DISC;

  if (cardType && startsWith(cardNumber, '*')) {
    return cardType.toUpperCase();
  }
  return null;
};
export const getLabelWithComponent = (message = '', param = []) => {
  const test = message.split(/\{(.?)\}/gi);
  each(param, (value, key) => {
    const findIndex = indexOf(test, `${key}`);
    if (findIndex !== -1) {
      test[findIndex] = value;
    }
  });
  return test;
};
export const signOut = (signOutUrl = '') => {
  sessionRemoveItem(USER_ID_KEY);
  sessionRemoveItem(SOLD_TO_KEY);
  if (!isEmpty(signOutUrl)) {
    window.location.href = signOutUrl;
  }
};
export const convertFileIntoBase64 = async file => {
  const filePromise = new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result);
    reader.onerror = error => reject(error);
  });
  return filePromise.then(result => result).catch(error => error);
};
export const scrollToTop = () => window.scrollTo(0, 0);
export const isCountryEnabled = () => {
  const store = getStore();
  const countryEnabledStatus = get(
    store,
    ['global', 'globalData', 'countryStatus', 'Page Content', 'countryCommerceConfig'],
    FULL_ENABLED
  );
  const soldTo = get(store, ['global', 'globalData', 'userDetails', 'soldTo'], []);
  const dummySoldtoData = get(store, ['global', 'globalData', 'userDetails', 'dummySoldto'], '');
  const loginUserStatus = isUserLoggedIn();
  const userStatus = loginUserStatus ? soldTo.length > 0 || !isEmpty(dummySoldtoData) : true;
  return (
    (countryEnabledStatus === FULL_ENABLED && userStatus) ||
    (countryEnabledStatus === PARTIAL_ENABLED && soldTo.length > 0)
  );
};
export const cartNotFoundStatus = (error = []) => {
  let status = false;
  if (!isEmpty(error) && error.length > 0) {
    const cart = error.find(
      item =>
        item.code === ERROR_CODE.WTR_ERR_001 ||
        item.code === ERROR_CODE.WAT_ERR_1001 ||
        item.code === ERROR_CODE.WAT_PRICE_001 ||
        item.code === ERROR_CODE.WAT_ERR_0007
    );
    status = !isEmpty(cart);
  }
  return status;
};
export const endEprocSession = () => {
  const punchoutSession = sessionGetItem(PUNCHOUT_SETUP);
  if (punchoutSession) {
    const { redirectUrl } = punchoutSession;
    const systemNotificationStatus = sessionGetItemValue(DISMISS_SYSTEM_NOTIFICATION_STORAGE);
    storageSessionClear();
    storageClear();
    if (!isEmpty(systemNotificationStatus)) {
      sessionSetItemValue(DISMISS_SYSTEM_NOTIFICATION_STORAGE, systemNotificationStatus);
    }
    window.location.href = redirectUrl;
  }
};
export const removeUrlPathVariable = (url, parameter) => {
  const urlParts = url.split('?');

  if (urlParts[1]) {
    const prefix = `${encodeURIComponent(parameter)}=`;
    const pars = urlParts[1].split(/[&;]/g);
    const filteredVariables = pars.filter(value => value.lastIndexOf(prefix) === -1);
    const pathVars = filteredVariables.length ? `?${filteredVariables.join('&')}` : '';
    return `${urlParts[0]}${pathVars}`;
  }
  return url;
};
export const removeQueryString = (url, parameter, replaceHistory = false) => {
  const query = get(window, ['location', 'search'], '').substring(1);
  if (
    query.length &&
    window.history !== undefined &&
    window.history.pushState !== undefined &&
    window.history.replaceState !== undefined
  ) {
    const filteredUrl = parameter
      ? removeUrlPathVariable(url, parameter)
      : window.location.pathname;
    if (replaceHistory) {
      window.history.replaceState({}, get(document, 'title', ''), filteredUrl);
    } else {
      window.history.pushState({}, get(document, 'title', ''), filteredUrl);
    }
  }
};
/**
 * Functional to update the children content type
 * @param {string} name Data from AEM page
 * @param {array} pageChildrens children array in page content
 * @param {object} pageData Data from AEM page
 * @param {boolean} isFooter if type is footer
 * @returns {array} Return pageChildrens which having in header, footer and root
 */
export const setPageContentType = (name, pageChildrens, pageData, isFooter) => {
  let type = get(pageData, ['Page Content', name], {});
  if (!isEmpty(type)) {
    if (isFooter) {
      const layoutcontainer = get(pageData, ['Page Content', name, 'par', 'layoutcontainer']);
      if (!isEmpty(layoutcontainer)) {
        const footerRoot = { root: layoutcontainer };
        const parRoot = { par: footerRoot };
        type = Object.assign(type, parRoot);
      }
    }
    type.name = name;
    pageChildrens.push(type);
  }
};
/**
 * Functional to update the page data content from AEM
 * @param {object} pageData Data from AEM page
 * @returns {object} Return pageContent which used in header, footer and labels
 */
export const upgradeAEMPageContent = pageData => {
  const pageChildrens = [];
  let pageContent = get(pageData, ['Page Content'], {});
  const pageNavigationContent = get(pageData, ['Navigation Content'], {});
  let pageContentNew = {};
  if (!isEmpty(pageData)) {
    setPageContentType('header', pageChildrens, pageData, false);
    setPageContentType('root', pageChildrens, pageData, false);
    setPageContentType('footer', pageChildrens, pageData, true);
    const pageChildren = { _children: pageChildrens };
    pageContent = Object.assign(pageContent, pageChildren);
    pageContentNew = {
      'Page Content': pageContent,
      'Navigation Content': pageNavigationContent,
    };
  }
  return pageContentNew;
};
export const getUserDetailsFromContract = (userResponse = {}) => {
  const result = userDetailsContract;
  each(userDetailsContract, (value, key) => {
    if (key === MAILING_ADDRESS_COUNTRY_CODE_KEY) {
      const userAddressData = get(userResponse, USER_ADDRESS, []);
      const countryCode = userAddressData.find(item => item.addressType === USER_ADDRESS_TYPE);
      result[key] = get(countryCode, ['countryCode'], '');
    } else {
      result[key] = userResponse[key];
    }
  });
  if (isEProcurementApp()) {
    const { localeLanguage } = getDefaultLocaleCountry();
    result.isoCode = localeLanguage;
    result.localeLanguage = localeLanguage;
  }
  return result;
};
export const getBoolValue = (value = '') =>
  typeof value === 'boolean' ? value : value.toLowerCase() === 'true';

export const getLocalCountryLanguage = () => {
  try {
    const objRes = {};
    if (isEProcurementApp()) {
      const store = getStore();
      const userDetails = get(store, ['global', 'globalData', 'userDetails'], {});
      if (Object.keys(userDetails).length > 0) {
        const { isoCode, localeCountry, localeLanguage } = userDetails;
        // en_XG
        const arrIsoCode = getTernaryCondition(isoCode, isoCode.split('_'), [
          localeLanguage,
          localeCountry,
        ]);
        objRes.localeLanguage = arrIsoCode[0] ? arrIsoCode[0].toLowerCase() : DEFAULT_LOCALE;
        objRes.localeCountry = arrIsoCode[1]
          ? arrIsoCode[1].toLowerCase()
          : getOrCondition(localeCountry, DEFAULT_COUNTRY_CODE).toLowerCase();
      } else {
        objRes.localeCountry = DEFAULT_COUNTRY_CODE;
        objRes.localeLanguage = DEFAULT_LOCALE;
      }
    } else {
      const pathVars = getDefaultLocaleCountry() || {};
      objRes.localeCountry = get(pathVars, 'localeCountry', DEFAULT_COUNTRY_CODE);
      objRes.localeLanguage = get(pathVars, 'localeLanguage', DEFAULT_LOCALE);
    }
    return objRes;
  } catch (e) {
    return {
      localeCountry: DEFAULT_COUNTRY_CODE,
      localeLanguage: DEFAULT_LOCALE,
    };
  }
};

export const isEProcurementUserOnECommerce = () => {
  const store = getStore();
  const userRole = get(store, ['global', 'globalData', 'userDetails', 'userRole', 'role'], '');
  return !isEProcurementApp() && userRole === EPROC_USER_ROLE;
};
/**
 * To handle an exception case where host names are different in dev environment of onprem and will be removed once it is decommissioned.
 */
export const getRelativeUrls = domain => lowerEnvSitesArray.find(site => site === domain);
/**
 * Functional to get domain of relative OR absolute
 * @param {string} apiEndPoint page date apiEndPoint
 * @returns {string} Return api URL
 */
export const setDomainName = apiEndPoint => {
  if (apiEndPoint.indexOf('http') > -1) {
    return apiEndPoint;
  }
  let domain = '';
  if (document.referrer) {
    const url = new URL(document.referrer);
    domain = `${url.protocol}//${url.hostname}`;
  }
  return getRelativeUrls(domain) ? `${domain}${apiEndPoint}` : apiEndPoint;
};
export const getViewCartURL = () => {
  let viewCartURL = '';
  const defaultURL = getDefaultLocaleCountry();
  defaultURL.template = CART_URL;
  const { origin } = window.location;
  each(defaultURL, value => {
    viewCartURL = viewCartURL ? `${viewCartURL}/${value}` : `${origin}/${value}`;
  });
  return viewCartURL;
};
/**
 * Functional to check user is loggedIn and Ecomm
 */
export const isEcommUserLoggedIn = () => {
  const data = getCookie(LOGIN_COOKIE);
  const userStatus = isUserLoggedIn();
  return data && !userStatus && !isEProcurementApp();
};
export const getPostAsteriskCountry = () => {
  let status = false;
  const pathVars = getDefaultLocaleCountry() || {};
  const countryCode = get(pathVars, 'localeCountry', '');
  const data = find(POST_ASTERISK_COUNTRY_CODES, item => item === countryCode);
  if (!isEmpty(data)) {
    status = true;
  }
  return status;
};
export const getLabelsWithAsterisk = (labels, isRequired = false) => {
  let text = labels;
  const store = getStore();
  const labelObject = get(store, ['global', 'globalData', 'labels'], {});
  const { optional, departmentLab, building, floor, room } = labelObject;
  if (isRequired) {
    const isPostAsteriskCountry = getPostAsteriskCountry();
    text = isPostAsteriskCountry ? `*${labels}` : `${labels}*`;
  } else if (
    labels === departmentLab ||
    labels === building ||
    labels === floor ||
    labels === room
  ) {
    return (
      <span className="label-optional-container">
        <span>{labels}</span>
        <span
          className="optional-text label-optional-text"
          data-locator="building-lab-department-label-optional"
        >
          {` (${optional}) `}
        </span>
      </span>
    );
  }
  return text;
};
export const loadCartProxyStatus = (action = {}, isCartMerge = false) => {
  const { type = '' } = action;
  let status = false;
  const { template = '' } = getDefaultLocaleCountry();
  const loginStatus = isUserLoggedIn();
  if (template === QUICK_ORDER_URL && type !== LOAD_SHOPPING_CART_DATA && !loginStatus) {
    status = true;
  }
  if (
    template === QUICK_ORDER_URL &&
    type !== LOAD_SHOPPING_CART_DATA &&
    loginStatus &&
    !isCartMerge
  ) {
    status = true;
  }
  return status;
};
export const isUnavailableItem = (code, errors = []) => {
  const error = errors.find(p => p.id === code);
  return error && (error.code === ERROR_CODE.WAT_SAP_382 || error.code === ERROR_CODE.WAT_ERR_382);
};
export const getDefaultIsoCode = () => `${getLocale()}_${getLocaleCountry().toUpperCase()}`;
export const getUrlWithParams = urlPath => {
  const pathVars = {
    localeCountry: getLocaleCountry(),
    localeLanguage: getLocale(),
  };

  return setUrlPathVariables(urlPath, pathVars);
};
export const getDefaultPayload = (data, defaultPayload) => {
  const payload = defaultPayload;
  each(payload, (item, key) => {
    payload[key] = data[key];
  });
  return payload;
};
export const numberOnly = quoteId => {
  const reg = /(^\d+$|^$)/;
  return reg.test(quoteId) ? quoteId : '';
};
export const redirectTo = (
  url = '',
  callback = () => {
    /* default callBack handler */
  }
) => {
  const store = getStore();
  const history = get(store, ['global', 'globalData', 'route', 'history'], undefined);
  if (history) {
    history.push(url);
    callback();
  }
};
export const getPOTaxFileCount = (attachmentDetails = []) => {
  let PurchaseOrderFile = 0;
  let TaxCertificateFile = 0;
  if (!isEmpty(attachmentDetails)) {
    forEach(attachmentDetails, value => {
      const attachmentType = get(value, 'attachmentType', '');
      if (attachmentType === TAX_EXEMPTION_ATTACHMENT_TYPE) {
        TaxCertificateFile = 1;
      }
      if (attachmentType === PURCHASE_ORDER_ATTACHMENT_TYPE) {
        PurchaseOrderFile = 1;
      }
    });
  }
  return { PurchaseOrderFile, TaxCertificateFile };
};
export const getQuoteDetailsResponse = quote => {
  const quoteData = quote;
  const quotes = get(quote, 'quotes', {});
  const { shipToInfo, billToInfo } = quotes;
  if (!isEmpty(quotes)) {
    if (!isEmpty(shipToInfo)) {
      shipToInfo.isDefault = true;
      quoteData.quotes.shipToInfo = [shipToInfo];
    }
    if (!isEmpty(billToInfo)) {
      billToInfo.isDefault = true;
      quoteData.quotes.billToInfo = [billToInfo];
    }
    quoteData.quotes.isQuoteShipToAddressMerged = false;
    quoteData.quotes.isQuoteBillToAddressMerged = false;
  }
  return quoteData;
};
export const getQuoteIdFromSession = () => sessionGetItemValue(QUOTE_ID_KEY);
const getAddress = (addressType, addressTypeBilling, isAddressMerge) =>
  addressType === addressTypeBilling && isAddressMerge;
export const getQuoteDataWithAddress = (quote, checkoutData, addressType) => {
  const quoteData = quote;
  const quotes = get(quote, 'quotes', {});
  const isQuoteShipToAddressMerged = get(quotes, 'isQuoteShipToAddressMerged', false);
  const isQuoteBillToAddressMerged = get(quotes, 'isQuoteBillToAddressMerged', false);
  const { shipToInfo = [], billToInfo = [] } = checkoutData;
  const shipToPartnerNumber = get(quotes, 'shipToInfo.[0].partnerNumber', '');
  const billToPartnerNumber = get(quotes, 'billToInfo.[0].partnerNumber', '');
  if (addressType === ADDRESS_TYPE.SHIPPING && !isQuoteShipToAddressMerged) {
    if (shipToInfo.length > 0) {
      forEach(shipToInfo, item => {
        const partnerNumber = get(item, 'partnerNumber', '');
        if (partnerNumber !== shipToPartnerNumber) {
          const address = item;
          address.isDefault = false;
          quoteData.quotes.shipToInfo.push(address);
        }
      });
    }
    quoteData.quotes.isQuoteShipToAddressMerged = true;
  }
  if (getAddress(addressType, ADDRESS_TYPE.BILLING, !isQuoteBillToAddressMerged)) {
    if (billToInfo.length > 0) {
      forEach(billToInfo, item => {
        const partnerNumber = get(item, 'partnerNumber', '');
        if (partnerNumber !== billToPartnerNumber) {
          const address = item;
          address.isDefault = false;
          quoteData.quotes.billToInfo.push(address);
        }
      });
    }
    quoteData.quotes.isQuoteBillToAddressMerged = true;
  }
  return quoteData;
};
export const getQuoteError = (errors = []) => {
  const errExclusionList = [ERROR_CODE.WAT_IMAGE_001, ERROR_CODE.WAT_ERR_0005];
  return errors ? errors.filter(e => errExclusionList.indexOf(e.code) === -1) : [];
};
export const isQuoteFeatureEnabled = () => {
  const store = getStore();
  const globalData = get(store, ['global', 'globalData'], null);
  const quoteDisabled = get(globalData, ['countryConfig', 'quoteDisabled'], false);
  return !quoteDisabled;
};
export const getRecentlySearchedItemsList = () => {
  const recentSearchesKey = isEProcurementApp()
    ? EPROC_RECENTLY_SEARCHED_KEY
    : RECENTLY_SEARCHED_KEY;

  const recentSearchesValue = window.localStorage.getItem(recentSearchesKey);
  return recentSearchesValue ? JSON.parse(recentSearchesValue) : [];
};
export const getCustomerBlockedError = (error = {}) => {
  let errorData = error;
  const errorCode = get(errorData, ['code'], '');
  if (!isEmpty(errorData)) {
    const store = getStore();
    const config = get(store, ['global', 'globalData', 'config'], {});
    const labels = get(store, ['global', 'globalData', 'labels'], {});
    const {
      WAT_SAP_154,
      WAT_ERR_154,
      WAT_HTTP_500,
      WAT_ERR_500,
      WAT_SAP_117,
      WAT_ERR_117,
      WAT_ERR_046,
    } = ERROR_CODE;
    const { contactWaters } = labels;
    const { contactUsLink } = config;
    let code = errorCode;
    let link = [];
    const contactWatersLink = {
      text: contactWaters,
      url: contactUsLink,
      target: '_blank',
    };
    if (errorCode === WAT_SAP_154 || errorCode === WAT_ERR_154) {
      code = 'creditBlockedErrorMsg';
      link = [contactWatersLink];
    }
    if (errorCode === WAT_HTTP_500 || errorCode === WAT_ERR_500) {
      code = 'placeOrderTimeoutError';
      link = [contactWatersLink];
    }
    if (errorCode === WAT_SAP_117 || errorCode === WAT_ERR_117 || errorCode === WAT_ERR_046) {
      link = [contactWatersLink];
    }
    if (errorCode === QUICK_ORDER_ERROR) {
      code = QUICK_ORDER_ERROR;
      const result = Object.keys(errorData)
        .filter(key => {
          const value = errorData[key];
          return typeof value === 'object' && value !== null && !Array.isArray(value);
        })
        .map(key => errorData[key]);
      link = [result];
    }
    window.scrollTo(0, 0);
    errorData = {
      code,
      link,
      errorCode,
    };
  }
  return errorData;
};
const isShippingSkeleton = (isCheckoutDataAvailable, isShippingSectionLoaded, checkoutData) => {
  let status = false;
  if (!isCheckoutDataAvailable || isShippingSectionLoaded) {
    status = true;
  }
  if (isCheckoutDataAvailable && isEmpty(checkoutData)) {
    status = false;
  }
  return status;
};
export const isDisplayCheckoutSkeleton = props => {
  const isCheckoutDataAvailable = get(props, ['checkoutData', 'isCheckoutDataAvailable'], false);
  const selectedBillTo = get(props, ['selectedBillTo'], '');
  const isPaymentSectionLoaded = get(props, ['isPaymentSectionLoaded'], true);
  const isShippingSectionLoaded = get(props, ['isShippingSectionLoaded', true]);
  const userPhoneNumber = get(props, 'checkoutData.userDetails.phone', '');
  const checkoutData = get(props, ['checkoutData', 'userDetails', 'customers'], []);
  let status = false;
  status = isShippingSkeleton(isCheckoutDataAvailable, isShippingSectionLoaded, checkoutData);
  if (
    isCheckoutDataAvailable &&
    !isEmpty(selectedBillTo) &&
    !isEmpty(userPhoneNumber) &&
    isPaymentSectionLoaded
  ) {
    status = true;
  }
  return status;
};
export const getSummaryDataStatus = (data = {}) => {
  let status = false;
  const { subTotal, totalItems, totalShippingAndHandling, totalPriceWithTax } = data;
  if (
    isEmpty(data) ||
    (isEmpty(subTotal) &&
      isEmpty(totalItems) &&
      isEmpty(totalPriceWithTax) &&
      isEmpty(totalShippingAndHandling))
  ) {
    status = true;
  }
  return {
    orderSummaryDataStatus: status,
    hideOrderSummarySection: status ? 'hide-checkout-sections' : '',
  };
};
export const getCreditCardLastFourDigit = creditCardTokenNum => {
  let digit = '';
  if (!isEmpty(creditCardTokenNum)) {
    const splitData = creditCardTokenNum.split('-');
    digit = splitData.pop();
  }
  return digit;
};
export const showLoaderRestriction = (showLoader = false) => {
  const pathVars = getDefaultLocaleCountry();
  const pageName = get(pathVars, 'template', '');
  let status = true;
  if (pageName === CHECKOUT_URL || showLoader) {
    status = false;
  }
  return status;
};
export const getAppChannel = () => {
  const isEProcApp = isEProcurementApp();
  return isEProcApp ? EPROC_USER_ROLE : ECOMM_USER_ROLE;
};
export const getContactUsLink = (eProcContactUsLink, eCommContactUsLink) =>
  isEProcurementApp() ? eProcContactUsLink : eCommContactUsLink;
export const removeCompetitionBlockedError = (error = {}) => {
  const errorData = error;
  const content = [];
  if (!isEmpty(errorData)) {
    const { WAT_ERR_117, WAT_ERR_046 } = ERROR_CODE;
    errorData.forEach(item => {
      if (item.code !== WAT_ERR_117 && item.code !== WAT_ERR_046) {
        content.push(item);
      }
    });
  }
  return content;
};
/* No changes are required in this file. */
